import { useQuery, useQueryClient } from "@tanstack/react-query";

import { getDesktopBridge, type DesktopBridge, type DesktopSettings, type DesktopSettingsPatch } from "./bridge.js";

const SETTINGS_KEY = ["desktop-settings"] as const;

export interface DesktopSettingsHandle {
  /** The live bridge, or `null` when not running in the desktop shell. */
  bridge: DesktopBridge | null;
  settings: DesktopSettings | undefined;
  isPending: boolean;
  /** Persists a patch through the bridge and updates the cached settings so the UI reflects it immediately. */
  update(patch: DesktopSettingsPatch): Promise<DesktopSettings>;
}

/**
 * Reads (and writes) the desktop shell's settings via `window.desktop`. Returns
 * `bridge: null` on the hosted deployment, where there is no shell — callers gate
 * their desktop-only UI on that. The query is disabled when there is no bridge,
 * so it never fires a rejected `settings.get()` on hosted.
 */
export function useDesktopSettings(): DesktopSettingsHandle {
  const bridge = getDesktopBridge();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: SETTINGS_KEY,
    queryFn: () => bridge!.settings.get(),
    enabled: bridge !== null,
    staleTime: Infinity,
  });

  async function update(patch: DesktopSettingsPatch): Promise<DesktopSettings> {
    if (!bridge) throw new Error("desktop settings are not available outside the desktop shell");
    const next = await bridge.settings.update(patch);
    queryClient.setQueryData(SETTINGS_KEY, next);
    return next;
  }

  return { bridge, settings: query.data, isPending: bridge !== null && query.isPending, update };
}
